import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LuBell, LuLogOut } from 'react-icons/lu';
import GreenChargeLogo from '../components/Common/GreenChargeLogo';
import ThemeToggle from '../theme/ThemeToggle';
import { PRIMARY_NAV, SECONDARY_NAV } from './navConfig';
import { useAuth } from '../context/AuthContext';
import { useSession } from '../context/SessionContext';
import { useNavigate } from 'react-router-dom';
import { useUnreadNotifications } from '../hooks/useUnreadNotifications';
import { useOptionalNotificationDrawer } from './NotificationDrawer/NotificationDrawerContext.jsx';
import './DesktopSidebar.less';

function SidebarLink({ item, rail }) {
  const { pathname } = useLocation();
  const Icon = item.icon;
  const activeMatcher = item.match ?? ((p) => p === item.to || p.startsWith(item.to + '/'));
  const isActive = activeMatcher(pathname);
  return (
    <NavLink
      to={item.to}
      className={`gc-sidebar-link${isActive ? ' is-active' : ''}`}
      aria-current={isActive ? 'page' : undefined}
      title={rail ? item.label : undefined}
    >
      <span className="gc-sidebar-icon" aria-hidden="true"><Icon /></span>
      {!rail && <span className="gc-sidebar-label">{item.label}</span>}
    </NavLink>
  );
}

function DesktopSidebar({ rail = false }) {
  const auth = useAuth();
  const session = useSession();
  const navigate = useNavigate();
  const { unread, hasUnread } = useUnreadNotifications();
  const drawer = useOptionalNotificationDrawer();

  const activeSessionUid = session?.state?.sessionUid || null;
  const primary = PRIMARY_NAV.map((item) => (
    item.key === 'charge' && activeSessionUid
      ? { ...item, to: '/charge/active', label: 'Active session' }
      : item
  ));

  const bellLabel = hasUnread
    ? `Open notifications, ${unread} unread`
    : 'Open notifications';
  const isDrawerOpen = drawer.isOpen === true;

  async function onSignOut() {
    try { await auth.logout(); } catch { /* best-effort */ }
    navigate('/auth', { replace: true });
  }

  return (
    <aside className={`gc-sidebar${rail ? ' is-rail' : ''}`} aria-label="Primary">
      <div className="gc-sidebar-brand">
        {rail
          ? <span className="gc-sidebar-brand-mark" aria-hidden="true">gc</span>
          : <GreenChargeLogo variant="wordmark" />
        }
      </div>

      <nav className="gc-sidebar-nav">
        <ul className="gc-sidebar-list">
          {primary.map((item) => (
            <li key={item.key}><SidebarLink item={item} rail={rail} /></li>
          ))}
        </ul>
        <div className="gc-sidebar-divider" aria-hidden="true" />
        <ul className="gc-sidebar-list">
          {SECONDARY_NAV.map((item) => (
            <li key={item.key}><SidebarLink item={item} rail={rail} /></li>
          ))}
        </ul>
      </nav>

      <div className="gc-sidebar-footer">
        <div className="gc-sidebar-footer-row">
          <ThemeToggle />
          <button
            type="button"
            onClick={drawer.open}
            className={`gc-sidebar-bell${isDrawerOpen ? ' is-open' : ''}`}
            aria-label={bellLabel}
            aria-haspopup="dialog"
            aria-expanded={isDrawerOpen}
            title="Notifications"
          >
            <LuBell aria-hidden="true" />
            {hasUnread && (
              <span className="gc-sidebar-bell__dot" aria-hidden="true" />
            )}
          </button>
          {!rail && (
            <button
              type="button"
              onClick={onSignOut}
              className="gc-sidebar-signout"
              aria-label="Sign out"
            >
              <LuLogOut aria-hidden="true" />
              <span>Sign out</span>
            </button>
          )}
          {rail && (
            <button
              type="button"
              onClick={onSignOut}
              className="gc-sidebar-signout is-icon"
              aria-label="Sign out"
              title="Sign out"
            >
              <LuLogOut aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}

export default DesktopSidebar;
